import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { PAGE_SIZE, avatarColour, initials, plural } from './listing';
import type { Paging, View } from './listing';

/**
 * The pieces every page of the admin area is built from, so Wells, Roles,
 * Layouts and Users look and behave like Company Management: the same dialog,
 * the same list/grid switch, the same footer under the list.
 */

export function Dialog({
  title,
  subtitle,
  size,
  busy,
  onClose,
  onSubmit,
  footer,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Wider than the default for a form with more to it than a handful of fields. */
  size?: 'mid' | 'wide';
  busy: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  footer: ReactNode;
  children: ReactNode;
}) {
  // Escape closes, as a dialog should; a click outside does not, so a slip of
  // the mouse cannot throw away a half-filled form.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div className="cmoverlay">
      <form
        className={size ? `cmdialog cmdialog--${size}` : 'cmdialog'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onSubmit={onSubmit}
        noValidate
      >
        <header>
          <span>
            <strong>{title}</strong>
            {subtitle && <small>{subtitle}</small>}
          </span>
          <button type="button" className="cmclose" aria-label="Close" onClick={onClose} disabled={busy}>
            ×
          </button>
        </header>
        <div className="cmdialog-body">{children}</div>
        <footer>
          <small>* Required fields</small>
          <span className="cmspacer" />
          {footer}
        </footer>
      </form>
    </div>
  );
}

/** Cancel and the one button that saves, as every dialog ends. */
export function DialogButtons({ busy, label, onClose }: { busy: boolean; label: string; onClose: () => void }) {
  return (
    <>
      <button type="button" className="cmbtn" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button type="submit" className="cmbtn cmbtn--primary" disabled={busy}>
        {busy ? 'Saving…' : label}
      </button>
    </>
  );
}

export function DialogError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div className="alert" role="alert">
      {error}
    </div>
  );
}

export function StatusChoice<T extends string>({
  legend,
  name,
  options,
  value,
  onChange,
}: {
  legend: string;
  name: string;
  options: { key: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <fieldset className="cmradios">
      <legend>{legend}</legend>
      {options.map((o) => (
        <label key={o.key} className={o.key === value ? 'cmradio is-on' : 'cmradio'}>
          <input type="radio" name={name} checked={o.key === value} onChange={() => onChange(o.key)} />
          {o.label}
        </label>
      ))}
    </fieldset>
  );
}

/** "Delete this …" at the foot of an edit dialog; it asks once before it does. */
export function DeleteLink({
  what,
  question,
  busy,
  onDelete,
}: {
  what: string;
  question: string;
  busy: boolean;
  onDelete: () => void;
}) {
  const [asking, setAsking] = useState(false);

  return (
    <p className="cmdelete">
      {asking ? (
        <>
          {question}{' '}
          <button type="button" className="cmbtn cmbtn--small cmbtn--danger" onClick={onDelete} disabled={busy}>
            Delete
          </button>{' '}
          <button type="button" className="cmbtn cmbtn--small" onClick={() => setAsking(false)}>
            Keep
          </button>
        </>
      ) : (
        <button type="button" className="adminlink" onClick={() => setAsking(true)}>
          Delete this {what}
        </button>
      )}
    </p>
  );
}

/** The "View As" switch at the right of the tools row. */
export function ViewToggle({ view, onChange }: { view: View; onChange: (next: View) => void }) {
  return (
    <div className="viewas">
      <span>View As</span>
      <button
        type="button"
        className={view === 'grid' ? 'viewas-btn is-active' : 'viewas-btn'}
        title="Grid View"
        aria-label="Grid View"
        aria-pressed={view === 'grid'}
        onClick={() => onChange('grid')}
      >
        <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
          <path
            d="M2 2h4v4H2zM8 2h4v4H8zM14 2h4v4h-4zM2 8h4v4H2zM8 8h4v4H8zM14 8h4v4h-4zM2 14h4v4H2zM8 14h4v4H8zM14 14h4v4h-4z"
            fill="currentColor"
          />
        </svg>
      </button>
      <button
        type="button"
        className={view === 'list' ? 'viewas-btn is-active' : 'viewas-btn'}
        title="List View"
        aria-label="List View"
        aria-pressed={view === 'list'}
        onClick={() => onChange('list')}
      >
        <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
          <path d="M2 4h16M2 8h16M2 12h16M2 16h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

/** "Showing 2 of 2 wells", and the pages when there is more than one. */
export function ListFoot<T>({ paging, one, many }: { paging: Paging<T>; one: string; many?: string }) {
  const { shown, total, pages, current, setPage } = paging;
  if (total === 0) return null;

  return (
    <footer className="cmfoot">
      <span>
        Showing {shown.length} of {total} {plural(total, one, many)}
        {pages > 1 && ` · ${PAGE_SIZE} per page`}
      </span>
      <span className="cmspacer" />
      {pages > 1 && (
        <span className="cmpager">
          <button type="button" className="cmbtn cmbtn--small" disabled={current === 1} onClick={() => setPage(current - 1)}>
            Previous
          </button>
          {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={n === current ? 'cmbtn cmbtn--small cmbtn--primary' : 'cmbtn cmbtn--small'}
              aria-current={n === current ? 'page' : undefined}
              onClick={() => setPage(n)}
            >
              {n}
            </button>
          ))}
          <button type="button" className="cmbtn cmbtn--small" disabled={current === pages} onClick={() => setPage(current + 1)}>
            Next
          </button>
        </span>
      )}
    </footer>
  );
}

/** The round initials beside a person's name. */
export function Avatar({ id, name }: { id: number; name: string }) {
  return (
    <span className="cmavatar" style={{ background: avatarColour(id) }} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

export function ActivePill({ active, offLabel = 'Inactive' }: { active: boolean; offLabel?: string }) {
  return <span className={active ? 'cmpill cmpill--active' : 'cmpill cmpill--inactive'}>{active ? 'Active' : offLabel}</span>;
}
